<template>
  <div class="tree-folder">
    <div
      :class="['tree-row tree-folder-row has-row-actions', { 'drag-over': dragOver, 'has-edit-actions': canEdit }]"
      :data-sidebar-folder-id="folder.id"
      :draggable="canEdit && !editing"
      @dragstart.stop="canEdit && !editing && $emit('dragStartFolder', folder)"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent
      @dragleave="dragOver = false"
      @drop.stop.prevent="drop"
    >
      <button
        type="button"
        :class="['tree-twisty', { expanded: folder.isExpanded }]"
        :title="folder.isExpanded ? 'Collapse folder' : 'Expand folder'"
        @click="$emit('toggleFolder', folder)"
      >
        <ChevronRight class="icon" />
      </button>
      <template v-if="editing">
        <Folder class="icon tree-icon" />
        <input
          ref="renameInput"
          v-model="draft"
          class="tree-rename-input"
          @click.stop
          @keydown.enter.prevent="commitRename"
          @keydown.esc.prevent="cancelRename"
          @blur="commitRename"
        />
      </template>
      <template v-else>
        <button
          type="button"
          class="tree-label"
          @click="$emit('toggleFolder', folder)"
          @dblclick.stop.prevent="beginRename"
        >
          <Folder class="icon tree-icon" />
          <span class="tree-name">{{ folder.name }}</span>
          <DocumentPresenceBadge v-if="!folder.isExpanded" :participants="folderPresence" />
        </button>
        <div class="tree-row-actions">
          <div class="tree-expansion-actions" :aria-label="`Expansion controls for ${folder.name}`" role="group">
            <button
              type="button"
              class="icon-button"
              title="Collapse folder and descendants"
              aria-label="Collapse folder and descendants"
              @click.stop="workspace.setFolderTreeExpanded(folder.id, false)"
            >
              <ChevronsDownUp class="icon" />
            </button>
            <button
              type="button"
              class="icon-button"
              title="Expand folder and descendants"
              aria-label="Expand folder and descendants"
              @click.stop="workspace.setFolderTreeExpanded(folder.id, true)"
            >
              <ChevronsUpDown class="icon" />
            </button>
          </div>
          <div v-if="canEdit" class="tree-actions">
            <AddMenu @new-document="$emit('createDocument', folder)" @new-folder="$emit('createFolder', folder)" />
            <button type="button" class="icon-button" title="Delete folder" @click.stop="$emit('deleteFolder', folder)">
              <Trash2 class="icon" />
            </button>
          </div>
        </div>
      </template>
    </div>
    <div v-if="folder.isExpanded" class="tree-children">
      <DocumentNode
        v-for="document in folder.documents"
        :key="document.id"
        :document="document"
        :can-edit="canEdit"
        :active-document-id="activeDocumentId"
        @open="$emit('openDocument', $event)"
        @rename="(documentId, title) => $emit('renameDocument', documentId, title)"
        @delete="$emit('deleteDocument', $event)"
        @drag-start="(document, canMove) => $emit('dragStartDocument', document, canMove)"
      />
      <FolderNode
        v-for="child in folder.folders"
        :key="child.id"
        :folder="child"
        :can-edit="canEdit"
        :active-document-id="activeDocumentId"
        @open-document="$emit('openDocument', $event)"
        @create-document="$emit('createDocument', $event)"
        @create-folder="$emit('createFolder', $event)"
        @rename-folder="(folderId, name) => $emit('renameFolder', folderId, name)"
        @rename-document="(documentId, title) => $emit('renameDocument', documentId, title)"
        @delete-folder="$emit('deleteFolder', $event)"
        @delete-document="$emit('deleteDocument', $event)"
        @drag-start-folder="$emit('dragStartFolder', $event)"
        @drag-start-document="(document, canMove) => $emit('dragStartDocument', document, canMove)"
        @drop-on-folder="$emit('dropOnFolder', $event)"
        @toggle-folder="$emit('toggleFolder', $event)"
      />
      <div v-if="folder.documents.length === 0 && folder.folders.length === 0" class="tree-empty">No items</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChevronRight, ChevronsDownUp, ChevronsUpDown, Folder, Trash2 } from 'lucide-vue-next';
import { computed, nextTick, ref } from 'vue';
import type { EntityId } from '../api/entityId';
import {
  useWorkspaceStore,
  type DocumentPresenceParticipant,
  type WorkspaceDocumentTree,
  type WorkspaceFolderTree
} from '../stores/workspace';
import AddMenu from './AddMenu.vue';
import DocumentNode from './DocumentNode.vue';
import DocumentPresenceBadge from './DocumentPresenceBadge.vue';

const props = defineProps<{
  folder: WorkspaceFolderTree;
  canEdit: boolean;
  activeDocumentId: EntityId | null;
}>();

const emit = defineEmits<{
  openDocument: [documentId: EntityId];
  createDocument: [folder: WorkspaceFolderTree];
  createFolder: [folder: WorkspaceFolderTree];
  renameFolder: [folderId: EntityId, name: string];
  renameDocument: [documentId: EntityId, title: string];
  deleteFolder: [folder: WorkspaceFolderTree];
  deleteDocument: [document: WorkspaceDocumentTree];
  dragStartFolder: [folder: WorkspaceFolderTree];
  dragStartDocument: [document: WorkspaceDocumentTree, canMove: boolean];
  dropOnFolder: [folder: WorkspaceFolderTree];
  toggleFolder: [folder: WorkspaceFolderTree];
}>();

const workspace = useWorkspaceStore();
const dragOver = ref(false);
const editing = ref(false);
const draft = ref('');
const renameInput = ref<HTMLInputElement | null>(null);
const folderPresence = computed(() => collectFolderPresence(props.folder));

function collectFolderPresence(folder: WorkspaceFolderTree): DocumentPresenceParticipant[] {
  const participants = new Map<EntityId, DocumentPresenceParticipant>();
  collectPresence(folder, participants);
  return [...participants.values()];
}

function collectPresence(
  folder: WorkspaceFolderTree,
  participants: Map<EntityId, DocumentPresenceParticipant>
): void {
  for (const document of folder.documents) {
    for (const participant of workspace.getDocumentPresence(document.id)) {
      const current = participants.get(participant.userId);
      if (current == null || (current.mode === 'Viewing' && participant.mode === 'Editing')) {
        participants.set(participant.userId, participant);
      }
    }
  }

  for (const child of folder.folders) {
    collectPresence(child, participants);
  }
}

function drop(): void {
  dragOver.value = false;
  emit('dropOnFolder', props.folder);
}

async function beginRename(): Promise<void> {
  if (!props.canEdit) {
    return;
  }

  draft.value = props.folder.name;
  editing.value = true;
  await nextTick();
  renameInput.value?.focus();
  renameInput.value?.select();
}

function commitRename(): void {
  if (!editing.value) {
    return;
  }

  editing.value = false;
  const name = draft.value.trim();
  if (name && name !== props.folder.name) {
    emit('renameFolder', props.folder.id, name);
  }
}

function cancelRename(): void {
  editing.value = false;
}
</script>
